import React, { useEffect, useRef, useState } from "react";
import { Link } from 'react-router-dom';
import Navbar from './navbar/Navbar';
import Footer from './footer/Footer';
import Collapsible from './Collapsible'; // Import shared component instead of duplicating
import { generateBrandedProductSlug } from '../utils/slugUtils';

/**
 * Radiance Blog Template – Tailwind + React
 * ---------------------------------------------------------------
 * • Sticky table of contents
 * • Fun header with eyebrow, title, byline, read time
 * • Top-of-article affiliate disclosure (auto-collapsible)
 * • In-article product cards with affiliate links (rel="sponsored nofollow noopener")
 * • Comparison table
 * • Pro/Con boxes, callouts, quote blocks
 * • Sticky share bar (desktop), floating CTA (mobile)
 * • Newsletter sign-up section
 * • Related posts grid
 * • FAQ schema + Article schema (JSON-LD)
 *
 * Drop this file into your project (e.g., /components/RadianceBlog.jsx)
 * Requires TailwindCSS. Icons use simple inline SVGs (no external deps).
 */

// ------------------------- Utility UI -------------------------
const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 rounded-full bg-custom-100/10 text-custom-100 px-3 py-1 text-xs font-medium tracking-wide border border-custom-100/20">
    <span className="h-1.5 w-1.5 rounded-full bg-custom-100"></span>
    {children}
  </div>
);

const Container = ({ children, className }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className ?? ""}`}>{children}</div>
);

const Card = ({children,className})=> (
  <div className={`rounded-3xl border border-gray-200 bg-white shadow-sm shadow-gray-200/40 ${className ?? ""}`}>{children}</div>
);

const Badge = ({children})=> (
  <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-600">{children}</span>
);

// ---------------------- Affiliate Disclosure ----------------------
const AffiliateDisclosure = ({ brandName = "Radiance" })=>{
  const [open,setOpen]=useState(false);
  return (
    <div className="my-4">
      <button onClick={()=>setOpen(v=>!v)} className="group flex w-full items-center gap-3 rounded-2xl bg-amber-50 px-4 py-3 text-left text-amber-900 hover:bg-amber-100">
        <svg className="h-5 w-5 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 16h-1v-4h-1"/><path d="M12 8h.01"/><circle cx="12" cy="12" r="9"/></svg>
        <span className="text-sm font-semibold">Affiliate Disclosure</span>
        <span className="ml-auto text-xs text-amber-800 opacity-80">{open?"Hide":"Show"}</span>
      </button>
      {open && (
        <div className="rounded-b-2xl border-x border-b border-amber-100 bg-white px-4 py-3 text-[13px] leading-6 text-amber-900">
          Some links on this page are affiliate links. If you click and purchase, {brandName} may earn a commission at no additional cost to you. Our recommendations are independent and based on testing, expert input, and verified reviews.
        </div>
      )}
    </div>
  );
}

// ------------------------- Table of Contents -------------------------
const TableOfContents = ({headings})=>{
  const [activeId, setActiveId] = React.useState('');

  React.useEffect(() => {
    // Set initial active section
    if (headings.length > 0) {
      setActiveId(headings[0].id);
    }

    const observerCallback = (entries) => {
      // Find all currently intersecting entries
      const intersectingEntries = entries.filter(entry => entry.isIntersecting);
      
      if (intersectingEntries.length > 0) {
        // Sort by intersection ratio and position, get the most prominent one
        const topEntry = intersectingEntries.reduce((prev, current) => {
          // Prefer the entry that's higher up on the page
          const prevTop = prev.boundingClientRect.top;
          const currentTop = current.boundingClientRect.top;
          
          // If both are in viewport, pick the one closest to the top
          if (prevTop > 0 && currentTop > 0) {
            return prevTop < currentTop ? prev : current;
          }
          
          // Otherwise use intersection ratio
          return prev.intersectionRatio > current.intersectionRatio ? prev : current;
        });
        
        setActiveId(topEntry.target.id);
      }
    };

    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: '-100px 0px -66% 0px',
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      headings.forEach((heading) => {
        const element = document.getElementById(heading.id);
        if (element) {
          observer.observe(element);
        }
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [headings]);

  // Auto-scroll the active TOC item into view
  React.useEffect(() => {
    if (activeId) {
      const activeLink = document.querySelector(`a[href="#${activeId}"]`);
      if (activeLink) {
        // For TOC active item, we don't need offset since it's in a sidebar
        // Just use smooth scroll to nearest position
        activeLink.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [activeId]);

  const handleTocClick = (e, headingId) => {
    e.preventDefault();
    const element = document.getElementById(headingId);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 80; // 80px for fixed header
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  if (headings.length === 0) return null;

  return (
    <aside className="hidden lg:block pt-8">
      <nav className="sticky top-24 h-max w-56 shrink-0 max-h-[calc(100vh-7rem)] overflow-y-auto scroll-smooth">
        <Card className="p-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-500">On this page</div>
          <ul className="space-y-2">
            {headings.map(h=> (
              <li key={h.id} className={h.level===2?"ml-0":"ml-3"}>
                <a 
                  href={`#${h.id}`}
                  onClick={(e) => handleTocClick(e, h.id)}
                  className={`block text-sm transition-all duration-200 py-1 ${
                    activeId === h.id 
                      ? 'text-[#ab3d22] font-semibold border-l-2 border-[#ab3d22] pl-2 -ml-2' 
                      : 'text-gray-700 hover:text-[#ab3d22]'
                  }`}
                >
                  {h.text}
                </a>
              </li>
            ))}
          </ul>
        </Card>
      </nav>
    </aside>
  );
}

// ------------------------- Product Card -------------------------
// Product shape: { id?, brand, name, imageUrl, price, rating?, reviewCount?, highlights?, affiliateUrl, badge?, categoryLabel?, pros?, cons?, keyIngredients?, attributes?, suitability? }

// Enhanced Badge Component matching existing design
const EnhancedBadge = ({ badge }) => {
  const getBadgeStyle = (badgeText) => {
    const lowerBadge = badgeText.toLowerCase();
    if (lowerBadge.includes('best') || lowerBadge.includes('editor')) {
      return "bg-blue-100 text-blue-800 border-blue-300";
    }
    if (lowerBadge.includes('award') || lowerBadge.includes('winner')) {
      return "bg-cyan-100 text-cyan-800 border-cyan-300";
    }
    if (lowerBadge.includes('trending') || lowerBadge.includes('popular')) {
      return "bg-slate-100 text-slate-800 border-slate-300";
    }
    if (lowerBadge.includes('derm') || lowerBadge.includes('approved')) {
      return "bg-custom-100/10 text-custom-100 border-custom-100/30";
    }
    return "bg-gray-100 text-gray-800 border-gray-300";
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${getBadgeStyle(badge)}`}>
      {badge}
    </span>
  );
};

// Enhanced Rating Stars matching existing design
const EnhancedRatingStars = ({ rating, reviewCount }) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  // Full stars
  for (let i = 0; i < fullStars; i++) {
    stars.push(
      <svg key={i} className="w-4 h-4 text-[#ab3d22] fill-current" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    );
  }
  
  // Empty stars
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
  for (let i = 0; i < emptyStars; i++) {
    stars.push(
      <svg key={`empty-${i}`} className="w-4 h-4 text-gray-300 fill-current" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center gap-0.5">
        {stars}
      </div>
      <span className="text-sm font-medium text-neutral-900 ml-1">
        {rating.toFixed(1)}
      </span>
    </div>
  );
};

const ProductCard = ({ product }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Check if there's any expandable content
  const hasExpandableContent = Boolean(
    product.pros?.length || 
    product.cons?.length || 
    product.keyIngredients?.length || 
    product.suitability?.length || 
    product.attributes?.length
  );

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Helper function to create brand slug for navigation
  const createBrandSlug = (brandName) => {
    return brandName.toLowerCase().replace(/[^a-z0-9]/g, '-');
  };

  const expandedSections = [
    { title: 'Pros', items: product.pros, type: 'pros' },
    { title: 'Cons', items: product.cons, type: 'cons' },
    { title: product.keyIngredients ? 'Key Ingredients' : 'Key Features', items: product.keyIngredients, type: 'ingredients' },
    { title: 'Best For', items: product.suitability, type: 'suitability' },
    { title: 'Attributes', items: product.attributes, type: 'attributes' }
  ].filter(section => section.items?.length);

  return (
    <article className="rounded-2xl border border-neutral-200 p-4 md:p-5 shadow-sm hover:shadow-md transition-shadow duration-200 bg-[#f8f8f8]">
      {/* Main Product Row */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start">
        {/* Product Image */}
        <div className="flex-shrink-0 self-start bg-white rounded-xl p-3">
          <img 
            src={product.imageUrl} 
            alt={`${product.brand} ${product.name}`}
            className="w-28 h-28 md:w-40 md:h-40 object-contain"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentNode.classList.add('bg-gray-200');
              e.target.parentNode.innerHTML = '<div class="w-28 h-28 md:w-40 md:h-40 rounded-xl bg-gray-200 flex items-center justify-center"><svg class="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"></path></svg></div>';
            }}
          />
        </div>

        {/* Product Info */}
        <div className="flex-grow min-w-0 flex-1">
          {/* Brand and Category */}
          <div className="flex items-center gap-2 mb-1">
            <Link
              to={`/products?brand=${encodeURIComponent(product.brand)}`}
              className="text-xs uppercase tracking-wide text-neutral-500 font-medium hover:text-[#ab3d22] transition-colors"
            >
              {product.brand}
            </Link>
            {product.categoryLabel && (
              <>
                <span className="text-neutral-300">•</span>
                <span className="text-xs text-neutral-500">{product.categoryLabel}</span>
              </>
            )}
          </div>

          {/* Product Name */}
          <h3 className="text-base md:text-lg font-semibold text-neutral-900 line-clamp-2 mb-2">
            {product.brand} {product.name}
          </h3>

          {/* Badge */}
          {product.badge && (
            <div className="flex flex-wrap gap-1 mb-3">
              <EnhancedBadge badge={product.badge} />
            </div>
          )}

          {/* Rating */}
          {product.rating && (
            <div className="mb-4">
              <EnhancedRatingStars rating={product.rating} reviewCount={product.reviewCount} />
            </div>
          )}

          {/* Highlights */}
          {product.highlights && product.highlights.length > 0 && (
            <div className="mb-4">
              <ul className="space-y-1 text-sm text-neutral-700">
                {product.highlights.slice(0, 3).map((highlight, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#ab3d22] mt-1.5 text-xs">●</span>
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Price and CTA */}
        <div className="flex flex-col md:items-end gap-3 md:text-right min-w-[120px]">
          {/* TEMPORARILY HIDDEN - Price needs updating */}
          {/* <div className="text-lg md:text-xl font-semibold text-neutral-900">
            {product.price}
          </div> */}
          
          <button
            onClick={() => window.open(product.affiliateUrl, '_blank', 'noopener,noreferrer')}
            className="inline-flex items-center justify-center bg-[#ab3d22] text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-[#8a2f1a] transition-colors focus:outline-none focus:ring-2 focus:ring-[#ab3d22] focus:ring-offset-2 whitespace-nowrap w-full md:w-auto"
            data-cta="shop_now"
            data-product={generateBrandedProductSlug(product.brand, product.name)}
            data-category={product.categoryLabel ? product.categoryLabel.toLowerCase().replace(/\s+/g, '-') : ''}
          >
            Shop Now
          </button>
          
          {hasExpandableContent && (
            <button
              onClick={handleToggleExpand}
              className="text-xs text-neutral-600 hover:text-[#ab3d22] transition-colors duration-200 flex items-center gap-1"
            >
              <span>{isExpanded ? 'Less info' : 'More info'}</span>
              <svg 
                className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Expandable Content - Now using shared Collapsible component */}
      {hasExpandableContent && (
        <Collapsible isOpen={isExpanded}>
          <div className="border-t border-neutral-200 mt-4 pt-4">
            <div className="max-h-80 overflow-y-auto pr-2">
              <div className="grid md:grid-cols-2 gap-4 text-sm text-neutral-700">
                {expandedSections.map((section) => (
                  <div key={section.type} className="space-y-2">
                    <h4 className="font-medium text-neutral-900 text-sm">
                      {section.title}
                    </h4>
                    <ul className={`space-y-1 ${
                      section.type === 'cons' ? 'text-red-600' : 
                      section.type === 'pros' ? 'text-green-600' : 
                      'text-neutral-600'
                    }`}>
                      {section.items?.map((item, index) => (
                        <li key={index} className="flex items-start gap-2">
                          {section.type === 'pros' && (
                            <svg className="w-3 h-3 mt-0.5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          {section.type === 'cons' && (
                            <svg className="w-3 h-3 mt-0.5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          )}
                          {(section.type === 'ingredients' || section.type === 'suitability' || section.type === 'attributes') && (
                            <span className="w-1 h-1 bg-neutral-400 rounded-full mt-2 flex-shrink-0" />
                          )}
                          <span className="text-xs leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Collapsible>
      )}
    </article>
  );
};

// ------------------------- Comparison Table -------------------------
const ComparisonTable = ({products})=> (
  <div className="my-6 w-full" style={{ marginLeft: 0, marginRight: 0 }}>
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Key Benefit</th>
              {/* TEMPORARILY HIDDEN - Price needs updating */}
              {/* <th className="px-4 py-3">Price</th> */}
              <th className="px-4 py-3">Rating</th>
              <th className="px-4 py-3">Link</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p,i)=> (
              <tr key={i} className="border-t border-gray-100">
                <td className="px-4 py-3 font-medium text-gray-900">{p.brand} {p.name}</td>
                <td className="px-4 py-3 text-gray-700">{p.highlights?.[0] ?? "—"}</td>
                {/* TEMPORARILY HIDDEN - Price needs updating */}
                {/* <td className="px-4 py-3">{p.price}</td> */}
                <td className="px-4 py-3">{typeof p.rating === "number" ? `${p.rating}/5` : "—"}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => window.open(p.affiliateUrl, '_blank', 'noopener,noreferrer')}
                    className="inline-flex items-center justify-center bg-[#ab3d22] text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-[#8a2f1a] transition-colors focus:outline-none focus:ring-2 focus:ring-[#ab3d22] focus:ring-offset-2"
                    data-cta="shop_now"
                    data-product={generateBrandedProductSlug(p.brand, p.name)}
                    data-category={p.categoryLabel ? p.categoryLabel.toLowerCase().replace(/\s+/g, '-') : ''}
                  >
                    Shop Now
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  </div>
);

// ------------------------- Callouts -------------------------
const ProTip = ({children})=> (
  <div className="my-6 rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4 text-sm text-emerald-900">
    <div className="mb-1 flex items-center gap-2 font-semibold"><svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12"/><path d="M8 11l4 4 4-4"/><path d="M12 21"/></svg>Pro tip</div>
    {children}
  </div>
);

const ProsCons = ({pros,cons})=> (
  <div className="grid gap-4 sm:grid-cols-2 my-6 w-full" style={{ marginLeft: 0, marginRight: 0 }}>
    <Card className="p-5">
      <div className="mb-2 font-semibold">Pros</div>
      <ul className="list-disc pl-5 text-sm text-zinc-700 space-y-1">
        {pros.map((p,i)=>(<li key={i}>{p}</li>))}
      </ul>
    </Card>
    <Card className="p-5">
      <div className="mb-2 font-semibold">Cons</div>
      <ul className="list-disc pl-5 text-sm text-zinc-700 space-y-1">
        {cons.map((c,i)=>(<li key={i}>{c}</li>))}
      </ul>
    </Card>
  </div>
);

// ------------------------- Share + Floating CTA -------------------------
const ShareBar = () => (
  <div className="sticky top-24 hidden lg:block">
    <div className="flex flex-col gap-3">
      {[
        {label:"Twitter", href:"https://twitter.com/intent/tweet"},
        {label:"Facebook", href:"https://www.facebook.com/sharer/sharer.php"},
        {label:"Pinterest", href:"https://pinterest.com/pin/create/button/"}
      ].map((s)=> (
        <a key={s.label} href={s.href} target="_blank" rel="noopener" className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-custom-100">{s.label[0]}</a>
      ))}
    </div>
  </div>
);

// ------------------------- Newsletter -------------------------
const Newsletter = () => {
  const handleSubscribe = (e) => {
    e.preventDefault();
    // Redirect to Mailchimp subscription form
    window.open('http://eepurl.com/jpTPXQ', '_blank', 'noopener,noreferrer');
  };

  return (
    <Card className="my-10 p-6 sm:p-8">
      <div className="flex flex-col gap-6">
        <div>
          <div className="text-sm font-semibold uppercase tracking-wider text-custom-100">Stay radiant</div>
          <h4 className="mt-2 text-xl font-bold" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>Weekly picks, ingredient explainers, and deals in your inbox</h4>
          <p className="mt-1 text-sm text-gray-600">No spam. Unsubscribe anytime.</p>
        </div>
        <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 w-full">
          <input 
            type="email" 
            placeholder="your@email.com" 
            className="h-11 flex-1 rounded-xl border-2 border-gray-200 bg-white px-4 text-sm outline-none focus:border-custom-100 transition-colors shadow-sm"
            required
          />
          <button 
            type="submit" 
            className="h-11 inline-flex items-center justify-center bg-[#ab3d22] hover:bg-[#8a2f1a] text-white font-medium px-6 rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#ab3d22] focus:ring-offset-2 whitespace-nowrap"
          >
            Subscribe
          </button>
        </form>
      </div>
    </Card>
  );
};

// ------------------------- Related Posts -------------------------
const RelatedPosts = ({items})=> (
  <div className="my-10">
    <h3 className="mb-4 text-lg font-bold" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>You might also like</h3>
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((it,i)=> (
        <a key={i} href={it.href} className="group block overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm hover:shadow-md">
          <img src={it.image} alt="" className="h-40 w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"/>
          <div className="p-4 text-sm font-medium text-gray-800 group-hover:text-custom-100">{it.title}</div>
        </a>
      ))}
    </div>
  </div>
);

// ------------------------- Main Layout -------------------------
// BlogMeta shape: { eyebrow, title, description, author, date, readTime, cover? }

const useHeadings = () => {
  const [headings,setHeadings]=useState([]);
  useEffect(()=>{
    const nodes = Array.from(document.querySelectorAll("article h2, article h3"));
    const hs = nodes.map(n=>({ id: n.id || (n.textContent||"").toLowerCase().replace(/[^a-z0-9]+/g,'-'), text: n.textContent||"", level: n.tagName === 'H2' ? 2 : 3 }));
    nodes.forEach((n,i)=>{ if(!n.id) n.id = hs[i].id; });
    setHeadings(hs);
  },[]);
  return headings;
}

export const BlogLayout = ({meta,children,products,relatedPosts})=>{
  const headings = useHeadings();
  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      <Navbar />
      <Container className="pb-8 pt-16 sm:pt-20 lg:pt-24">
        <h1 className="text-4xl/tight font-extrabold text-gray-900 sm:text-5xl" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700 }}>{meta.title}</h1>
        <p className="mt-6 text-gray-700">{meta.description}</p>
        <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-gray-600">
          <div className="flex items-center gap-2"><span className="h-6 w-6 rounded-full bg-custom-100"></span> {meta.author}</div>
          <span>•</span>
          <time>{meta.date}</time>
          <span>•</span>
          <span>{meta.readTime}</span>
        </div>
        
        {/* Affiliate Disclosure moved up */}
        <div className="mt-6">
          <AffiliateDisclosure />
        </div>
      </Container>
      
      {/* Cover image - full width outside container */}
      {meta.cover && (
        <div className="mt-0 mb-4">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Card className="overflow-hidden">
              <img src={meta.cover} alt={meta.title} className="h-[450px] sm:h-[550px] lg:h-[600px] w-full object-cover"/>
            </Card>
          </div>
        </div>
      )}

      <Container className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-[240px,1fr] xl:grid-cols-[280px,1fr]">
        <TableOfContents headings={headings} />
        <div className="min-w-0 w-full max-w-full">
          <article className="w-full max-w-full text-gray-800 leading-relaxed">
            <div 
              className="blog-content admin-content"
            >
              {children}
            </div>
          </article>

          {products && products.length>0 && (
            <div className="mt-10 space-y-8">
              <h2 id="featured-picks" className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>Featured Picks</h2>
              <div className="space-y-6">
                {products.map((p,i)=> <ProductCard key={i} product={p} />)}
              </div>
            </div>
          )}

          <Newsletter />
        </div>
      </Container>

      {relatedPosts && relatedPosts.length > 0 && (
        <Container>
          <RelatedPosts items={relatedPosts} />
        </Container>
      )}

      {/* JSON-LD Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: meta.title,
            description: meta.description,
            author: { '@type': 'Person', name: meta.author },
            datePublished: meta.date,
          })
        }}
      />

      <Footer />
    </div>
  );
}

// Export additional components for use in blog posts
export { ProTip, ProsCons, ProductCard, ComparisonTable, Card, Badge };
